// Pure domain transforms + print-payload assembly for the menu app. No I/O —
// this module imports only @lemontaigu/shared, so it is the deterministic core
// behind the MenuStore seam (pipeline-tdd-ioc §8.2) and is unit-testable offline.
import { COURSES, RESTAURANT, groupByCourse } from '@lemontaigu/shared';
import type {
  CourseSlug,
  DietaryTag,
  Dish,
  DishInput,
  GroupableDish,
  MenuSnapshot,
  MenuTypeSlug,
} from '@lemontaigu/shared';

// --- Raw PostgREST row shapes (snake_case) ---------------------------------

/** Raw `dishes` row as PostgREST returns it. */
export interface DishRow {
  id: number;
  name: string;
  description: string;
  price: string;
  allergens: string;
  dietary_tags: string[];
  is_course_note: boolean;
  is_active: boolean;
  course: string | null;
  menu_types: string[];
  ls_sku: string | null;
  sort_order: number;
  course_position: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Raw embedded `dishes` row inside a dish_servings join. */
export interface DishEmbed {
  name: string;
  description: string | null;
  is_active: boolean;
  deleted_at: string | null;
  is_course_note: boolean;
  course: string | null;
  menu_types: string[];
  course_position?: number;
}

/** One today/future serving: a date + its embedded dish row. */
export interface FutureServing {
  date: string;
  dish: DishEmbed;
}

export function rowToDish(r: DishRow): Dish {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? '',
    price: r.price ?? '',
    allergens: r.allergens ?? '',
    dietaryTags: (r.dietary_tags ?? []) as DietaryTag[],
    isCourseNote: !!r.is_course_note,
    isActive: !!r.is_active,
    course: (r.course ?? null) as CourseSlug | null,
    menuTypes: (r.menu_types ?? []) as MenuTypeSlug[],
    lsSku: r.ls_sku,
    sortOrder: r.sort_order ?? 0,
    coursePosition: r.course_position ?? 0,
    deletedAt: r.deleted_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/** camelCase patch → snake_case row, omitting untouched keys (partial update). */
export function inputToRow(p: Partial<DishInput>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (p.name !== undefined) row.name = p.name;
  if (p.description !== undefined) row.description = p.description;
  if (p.price !== undefined) row.price = p.price;
  if (p.allergens !== undefined) row.allergens = p.allergens;
  if (p.dietaryTags !== undefined) row.dietary_tags = p.dietaryTags;
  if (p.isCourseNote !== undefined) row.is_course_note = p.isCourseNote;
  if (p.isActive !== undefined) row.is_active = p.isActive;
  if (p.course !== undefined) row.course = p.course || null;
  if (p.menuTypes !== undefined) row.menu_types = p.menuTypes;
  if (p.lsSku !== undefined) row.ls_sku = p.lsSku || null;
  if (p.sortOrder !== undefined) row.sort_order = p.sortOrder;
  if (p.coursePosition !== undefined) row.course_position = p.coursePosition;
  return row;
}

// --- Print payload (the WP MENU_DATA shape) --------------------------------

export interface PrintItem {
  id: string;
  name: string;
  desc: string;
  price: string;
  courseSlug: string;
}
export interface PrintSection {
  id: string;
  title: string;
  courseSlug: string;
  items: PrintItem[];
  /** Course notes (is_course_note dishes) — rendered italic under the items. */
  notes: string[];
}
export interface PrintCourseIndex {
  slug: string;
  label: string;
  count: number;
}
export interface PrintMenu {
  subtitle: string;
  courses: PrintCourseIndex[];
  sections: PrintSection[];
  sides?: string;
}
export interface PrintDailyDay {
  sections: PrintSection[];
  courses: PrintCourseIndex[];
}
export interface PrintData {
  RESTAURANT: {
    name: string;
    fullName: string;
    logo: string;
    tagline: string;
    location: string;
    hours: string;
    note: string;
  };
  DAILY: { subtitle: string; byDate: Record<string, PrintDailyDay> };
  FORMULE: PrintMenu;
  CARTE: PrintMenu;
  meta: { today: string };
}

// Generic course names (Entrée / Plat principal / Dessert …) for the printer's
// Menu du Jour headings. The daily COURSE_LABELS are null (the website prints
// daily headingless), but the WP printer fell back to the course name.
const COURSE_NAME = new Map<string, string>(COURSES.map((c) => [c.slug, c.label]));
const courseName = (slug: string): string => COURSE_NAME.get(slug) ?? slug;

/** Build a formule/carte menu from the catalogue, grouped + ordered per shared rules. */
function buildMenu(
  dishes: Dish[],
  type: MenuTypeSlug,
  subtitle: string,
  extras?: { sides?: string },
): PrintMenu {
  const grouped = groupByCourse(
    dishes.filter((d) => d.menuTypes.includes(type)),
    type,
  );
  const sections: PrintSection[] = [];
  const courses: PrintCourseIndex[] = [];
  for (const sec of grouped) {
    const items: PrintItem[] = sec.items
      .filter((it) => !it.isCourseNote)
      .map((it, i) => ({
        id: `${type[0]}-${sec.course}-${i}`,
        name: it.name,
        desc: it.description || '',
        price: it.price || '',
        courseSlug: sec.course,
      }));
    // is_course_note dishes (e.g. the Formule "Garniture du jour…") render as
    // italic course notes under the course — not as a separate sides block.
    const notes = sec.items.filter((it) => it.isCourseNote).map((it) => it.name).filter(Boolean);
    if (!items.length && !notes.length) continue;
    sections.push({ id: sec.course, title: sec.label ?? '', courseSlug: sec.course, items, notes });
    if (items.length) courses.push({ slug: sec.course, label: sec.label ?? sec.course, count: items.length });
  }
  return { subtitle, courses, sections, ...(extras ?? {}) };
}

/** Build one day's daily payload from generic course items (prices blank, per WP). */
function dailyDayFromGroupable(items: GroupableDish[]): PrintDailyDay {
  const grouped = groupByCourse(items, 'daily');
  const sections: PrintSection[] = [];
  const courses: PrintCourseIndex[] = [];
  for (const sec of grouped) {
    const its: PrintItem[] = sec.items
      .filter((it) => !it.isCourseNote)
      .map((it, i) => ({
        id: `d-${sec.course}-${i}`,
        name: it.name,
        desc: it.description || '',
        price: '',
        courseSlug: sec.course,
      }));
    if (!its.length) continue;
    const heading = sec.label ?? courseName(sec.course);
    sections.push({ id: sec.course, title: heading, courseSlug: sec.course, items: its, notes: [] });
    courses.push({ slug: sec.course, label: heading, count: its.length });
  }
  return { sections, courses };
}

/** Pure assembly of the full print payload from already-fetched data. */
export function assemblePrintData(
  dishes: Dish[],
  history: MenuSnapshot[],
  futureServings: FutureServing[],
  today: string,
): PrintData {
  const FORMULE = buildMenu(dishes, 'formule', 'Toujours disponible, change occasionnellement');
  const CARTE = buildMenu(dishes, 'carte', 'Toujours disponible, plats à la carte');

  const byDate: Record<string, PrintDailyDay> = {};

  // Past days: from frozen snapshots.
  for (const snap of history) {
    byDate[snap.date] = dailyDayFromGroupable(
      snap.items.map((it) => ({
        name: it.title,
        description: it.description,
        price: '',
        isCourseNote: false,
        course: (it.course || null) as CourseSlug | null,
        coursePosition: it.rank,
      })),
    );
  }

  // Today + future: from live servings.
  const liveByDate = new Map<string, GroupableDish[]>();
  for (const { date, dish: d } of futureServings) {
    if (!d || !d.is_active || d.deleted_at || !(d.menu_types ?? []).includes('daily')) continue;
    const arr = liveByDate.get(date) ?? [];
    arr.push({
      name: d.name,
      description: d.description ?? '',
      price: '',
      isCourseNote: !!d.is_course_note,
      course: (d.course || null) as CourseSlug | null,
      coursePosition: d.course_position ?? 0,
    });
    liveByDate.set(date, arr);
  }
  for (const [date, items] of liveByDate) byDate[date] = dailyDayFromGroupable(items);

  return {
    RESTAURANT: {
      name: RESTAURANT.name,
      fullName: RESTAURANT.fullName,
      tagline: RESTAURANT.tagline,
      location: RESTAURANT.location,
      hours: RESTAURANT.hours,
      note: RESTAURANT.allergenNote,
      logo: './logo-montaigu.png',
    },
    DAILY: { subtitle: 'Renouvelé chaque jour selon les produits du marché', byDate },
    FORMULE,
    CARTE,
    meta: { today },
  };
}
